import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { TriggeredBy } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { Thread } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/models.pb';
import { motion } from 'motion/react';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useTranslation } from '@frontend/i18n';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { ThreadBodyHooks } from '@frontend/thread-body';
import { theme } from '@frontend/theme';
import { Button, Dot, useAlert } from '@frontend/design-system';

const MotionButton = motion(Button);
const MotionDot = motion(Dot);

type Props = {
  threadId: string;
  groupId: string;
  locationPhone: string;
};

export const AiAssistantButton = ({ threadId, groupId, locationPhone }: Props) => {
  const { t } = useTranslation('mini-chat');
  const alert = useAlert();
  const [botIsActive, setBotIsActive] = useState<boolean>(false);

  const {
    data: { allLocationPhones },
  } = InboxLocationPhones.Hooks.useLocationPhones();

  const hasDefaultAiNumber = useMemo<boolean>(() => {
    return (
      allLocationPhones?.some(
        (locationPhoneObj) => locationPhoneObj.locationPhone === locationPhone && locationPhoneObj.defaultAi
      ) ?? false
    );
  }, [locationPhone, allLocationPhones]);

  const { getThreadQuery } = ThreadBodyHooks.useGetThreadData({
    threadId: threadId || '',
    locationId: groupId,
  });

  const thread = getThreadQuery?.data?.pages?.[0]?.thread as Thread;
  const isActive = !!thread?.aiStatus?.isActive;

  const setBotInactiveMutation = SMSDataV4.Mutations.useSetBotInactiveMutation({
    options: {
      onSuccess: () => {
        alert.success(
          t('AI Receptionist is no longer active in this thread. It will return automatically in 30 days.')
        );
      },
    },
  });

  const handleToggle = useCallback(() => {
    if (botIsActive) {
      setBotInactiveMutation.mutate({
        locationId: groupId,
        threadId,
        triggeredBy: TriggeredBy.TRIGGERED_BY_WEAVE_USER,
      });
    }
    setBotIsActive(!botIsActive);
  }, [botIsActive, groupId, threadId, setBotInactiveMutation]);

  useEffect(() => {
    setBotIsActive(isActive);
  }, [isActive]);

  if (!hasDefaultAiNumber) return null;

  return (
    <motion.div
      css={{
        display: botIsActive ? 'flex' : 'none',
        alignItems: 'center',
      }}
    >
      <MotionButton
        css={[
          {
            padding: 0,
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          },
          aiAssistantStyles,
        ]}
        hoverLabel={t('AI Receptionist is active')}
        iconName='aiweave-assistant'
        size='small'
        variant='secondary'
        onClick={handleToggle}
      />
      <MotionDot
        color={botIsActive ? 'success' : 'neutral'}
        css={{
          position: 'static',
          display: 'inline-flex',
          transform: `translateY(0.2em) translateX(-0.2em)`,
        }}
      />
    </motion.div>
  );
};

export const aiAssistantStyles = css`
  border-radius: ${theme.borderRadius.medium};
  background: linear-gradient(
    -90deg,
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5},
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5}
  );
  background-size: 400% 400%;
  animation: gradient 5s ease infinite;

  &:hover:not(:disabled),
  &:focus:not(:disabled) {
    background: linear-gradient(
      -90deg,
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5},
      ${theme.colors.primary10},
      ${theme.colors.secondary.seaweed5}
    );
    background-size: 400% 400%;
    animation: gradient 5s ease infinite;
  }

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;
